import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { useHealthCounts } from 'src/features/monitoring/hooks/useHealthCounts';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { pageTitles } from 'src/features/navigation/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useParking } from 'src/providers/ParkingProvider';
import { useMediaQuery } from 'src/hooks/use-media-query';
import { toast } from 'src/registry/hooks/UseToast';
import {
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuLabel,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuItem,
} from 'src/registry/ui/DropdownMenu';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faParkingCircle, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { type Parking, MEMBERSHIP_ROLES, type UserMembership } from 'src/features/parking/types';
import { messages } from 'src/features/parking/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';
import { storage } from 'src/utils/storage';

interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    userMemberships: UserMembership[];
}

const Header = ({ userMemberships }: HeaderProps) => {
    const { formatMessage } = useIntl();
    const { parkingId, parkingName, setParkingId, setParkingName, setParkingRole } = useParking();

    const [pageTitle, setPageTitle] = React.useState('');
    const [currentErrorsChecked, setCurrentErrorsChecked] = React.useState(storage.getCurrentErrorsChecked());
    const storeTitle = useTitleStore((state) => state.title);

    const { pathname } = useLocation();
    const navigate = useNavigate();

    const isMobile = useMediaQuery('(max-width: 639px)');

    const iconSize = !isMobile ? 'smIcon' : 'mdIcon';

    const accessPayStations = useHasPermissions([PERMISSION.ACCESS_PAY_STATIONS_MONITORING]);
    const accessDevices = useHasPermissions([PERMISSION.ACCESS_DEVICES_MONITORING]);
    const canSeeHealth = !!parkingId && (accessPayStations || accessDevices);

    const { errors, warnings, isLoading: isLoadingHealth } = useHealthCounts(parkingId, canSeeHealth);

    const monitoringPath = React.useMemo(() => {
        if (accessDevices) return `/${PATH.MONITORING}/devices`;
        if (accessPayStations) return `/${PATH.MONITORING}/pay-station`;
        return `/${PATH.MONITORING}`;
    }, [accessDevices, accessPayStations]);

    React.useEffect(() => {
        // Skip while queries are still loading — otherwise the initial `errors = 0`
        // overwrites the persisted count, then the resolved value falsely registers
        // as an increase and pulses on every page load.
        if (isLoadingHealth) return;
        const previousErrors = storage.getCurrentErrors();
        if (errors > previousErrors) {
            storage.setCurrentErrorsChecked(false);
            setCurrentErrorsChecked(false);
        }
        if (errors !== previousErrors) {
            storage.setCurrentErrors(errors);
        }
    }, [errors, isLoadingHealth]);

    const pathRoot = React.useMemo(() => {
        // We need to get the first part of the pathname
        return pathname.split('/')[1] as PATH;
    }, [pathname]);

    React.useEffect(() => {
        // Map the page paths to a page title
        const getPageTitle = (path: PATH) => {
            switch (path) {
                // case PATH.VEHICLES:
                //     return formatMessage(pageTitles.vehicles);
                default:
                    return formatMessage(pageTitles.smartPark);
            }
        };

        let pathName = '';
        if (pathRoot) {
            pathName = getPageTitle(pathRoot);
        }

        setPageTitle(pathName || formatMessage(pageTitles.home));
    }, [formatMessage, pathRoot]);

    const handleParkingChange = (parking: Parking, role: MEMBERSHIP_ROLES) => {
        setParkingId(parking.id);
        setParkingName(parking.name);
        setParkingRole(role);
        void navigate('/');
        toast({
            title: formatMessage(messages.currentParkingChangeSuccess),
            description: (
                <FormattedMessage
                    {...messages.currentParkingChangeSuccessDetails}
                    values={{
                        parkingName: parking.name,
                        b: (chunks: React.ReactNode[]) => <b className="font-semibold">{chunks}</b>,
                    }}
                />
            ),
        });
    };

    return (
        <header className="-my-2 flex items-center justify-between gap-4 overflow-hidden bg-white py-2 sm:sticky sm:top-0 sm:z-30 sm:-mx-8 sm:px-8 dark:bg-gray-900">
            <h1 className="relative text-3xl font-semibold tracking-tight sm:text-nowrap">
                <div className="flex items-center gap-4">{storeTitle || pageTitle}</div>
            </h1>
            {canSeeHealth && (errors > 0 || warnings > 0) && (
                <div className="flex flex-wrap items-center gap-2">
                    {warnings > 0 && (
                        <Button size="mdIcon" variant="warning" asChild>
                            <Link to={monitoringPath}>
                                <FontAwesomeIcon icon={ICON_MAP.CIRCLE_EXCLAMATION} />
                                <span className="hidden md:block">
                                    <FormattedMessage
                                        {...parkingStatusMessages.warningsCount}
                                        values={{ count: warnings }}
                                    />
                                </span>
                            </Link>
                        </Button>
                    )}
                    {errors > 0 && (
                        <Button
                            className={cn(!currentErrorsChecked && 'animate-pulse hover:animate-none')}
                            size="mdIcon"
                            variant="destructive"
                            onClick={() => {
                                setCurrentErrorsChecked(true);
                                storage.setCurrentErrorsChecked(true);
                            }}
                            asChild
                        >
                            <Link to={monitoringPath}>
                                <FontAwesomeIcon icon={ICON_MAP.TRIANGLE_EXCLAMATION} />
                                <span className="hidden md:block">
                                    <FormattedMessage
                                        {...parkingStatusMessages.criticalErrorsCount}
                                        values={{ count: errors }}
                                    />
                                </span>
                            </Link>
                        </Button>
                    )}
                </div>
            )}
            {userMemberships?.length > 1 && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button size={iconSize} variant="sm">
                            <FontAwesomeIcon icon={faParkingCircle} className="text-base" />
                            <span className={`hidden ${!isMobile ? 'lg:block' : 'md:block'}`}>{parkingName}</span>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className={`hidden text-xs ${!isMobile ? 'lg:block' : 'md:block'}`}
                            />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="z-50" align="end">
                        <DropdownMenuLabel>{formatMessage(messages.parkings)}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <div className="max-h-60 overflow-y-auto">
                            {userMemberships?.map(({ parking, role }) => (
                                <DropdownMenuItem
                                    key={parking.id}
                                    onClick={() => {
                                        handleParkingChange(parking, role);
                                    }}
                                    className="flex flex-col items-start"
                                >
                                    <p>{parking.name}</p>
                                    <p className="text-xs text-gray-600">
                                        {formatMessage(messages.lastOpened)}
                                        {': '}
                                        {new Date(parking.updated_at).toLocaleString()}
                                    </p>
                                </DropdownMenuItem>
                            ))}
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </header>
    );
};

export default Header;
